import React, { useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { userOptions, getInvalidMessagePlaceholders, extractPlaceholdersFromMessage } from "./whatsappFlowCatalogs";
import {
  CONDITIONAL_OPERATORS,
  createEmptyCondition,
  createEmptyGroup,
  normalizeConditionalEntry,
  normalizeConditionalGroups,
  operatorNeedsValue,
  serializeConditionalGroups,
} from "./whatsappFlowConditional";

const appendTokenToMessageEnd = (currentMessage, token) => {
  let result = currentMessage || "";
  if (result.includes(token)) return result;
  if (!result) return token;
  const endsWithSpace = /\s$/.test(result);
  return endsWithSpace ? `${result}${token}` : `${result} ${token}`;
};

export default function WhatsappFlowConditionalSection({
  entry,
  entryIndex = 0,
  onPatchEntry,
  onRemove,
  fieldOptions = [],
  parameterOptions = [],
  showSyntaxHelp = false,
}) {
  const [syntaxExpanded, setSyntaxExpanded] = useState(false);

  const normalizedEntry = normalizeConditionalEntry(entry);
  const groups = normalizedEntry.conditionalGroups;
  const conditionalMessage = normalizedEntry.conditionalMessage;
  const conditionalRecipient = normalizedEntry.conditionalRecipient;

  const placeholderWarnings = useMemo(
    () => getInvalidMessagePlaceholders(conditionalMessage),
    [conditionalMessage]
  );

  const previewWhen = useMemo(
    () => serializeConditionalGroups(groups),
    [groups]
  );

  const patchEntry = (patch) => {
    onPatchEntry({ ...normalizedEntry, ...patch });
  };

  const patchGroups = (nextGroups) => {
    patchEntry({ conditionalGroups: normalizeConditionalGroups(nextGroups) });
  };

  const updateCondition = (groupIndex, conditionIndex, patch) => {
    const nextGroups = groups.map((group, gi) => {
      if (gi !== groupIndex) return group;
      return {
        conditions: group.conditions.map((condition, ci) =>
          ci === conditionIndex ? { ...condition, ...patch } : condition
        ),
      };
    });
    patchGroups(nextGroups);
  };

  const addConditionToGroup = (groupIndex) => {
    const nextGroups = groups.map((group, gi) =>
      gi === groupIndex
        ? { conditions: [...group.conditions, createEmptyCondition()] }
        : group
    );
    patchGroups(nextGroups);
  };

  const removeCondition = (groupIndex, conditionIndex) => {
    const nextGroups = groups
      .map((group, gi) => {
        if (gi !== groupIndex) return group;
        const nextConditions = group.conditions.filter((_, ci) => ci !== conditionIndex);
        return { conditions: nextConditions };
      })
      .filter((group) => group.conditions.length > 0);
    patchGroups(nextGroups);
  };

  const addGroup = () => {
    patchGroups([...groups, createEmptyGroup()]);
  };

  const removeGroup = (groupIndex) => {
    patchGroups(groups.filter((_, gi) => gi !== groupIndex));
  };

  const handleConditionalMessageChange = (event) => {
    const nextMessage = event.target.value;
    const prevKeys = normalizedEntry.conditionalParameterKeys || [];
    const tokensInMessage = extractPlaceholdersFromMessage(nextMessage);
    const merged = [...tokensInMessage];
    for (const key of prevKeys) {
      if (!merged.includes(key)) merged.push(key);
    }
    patchEntry({
      conditionalMessage: nextMessage,
      conditionalParameterKeys: merged,
    });
  };

  const insertConditionalParameter = (token) => {
    if (!token) return;
    const prevKeys = normalizedEntry.conditionalParameterKeys || [];
    const nextMessage = appendTokenToMessageEnd(conditionalMessage, token);
    const nextKeys = prevKeys.includes(token) ? prevKeys : [...prevKeys, token];
    patchEntry({
      conditionalMessage: nextMessage,
      conditionalParameterKeys: nextKeys,
    });
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: "12px" }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Typography variant="subtitle2" sx={{ color: "#161e55" }}>
          Mensaje condicional #{entryIndex + 1}
        </Typography>
        {onRemove ? (
          <IconButton
            size="small"
            onClick={onRemove}
            aria-label={`Eliminar mensaje condicional ${entryIndex + 1}`}
            color="error"
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        ) : null}
      </Stack>

      {normalizedEntry.parseError ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {normalizedEntry.parseError} Reconfigura las condiciones en el builder.
        </Alert>
      ) : null}

      <Stack spacing={2}>
        <FormControl fullWidth>
          <InputLabel id={`conditional-recipient-label-${entryIndex}`}>
            Destinatario condicional
          </InputLabel>
          <Select
            labelId={`conditional-recipient-label-${entryIndex}`}
            label="Destinatario condicional"
            value={conditionalRecipient}
            onChange={(event) =>
              patchEntry({ conditionalRecipient: event.target.value })
            }
          >
            <MenuItem value="">
              <em>Sin destinatario</em>
            </MenuItem>
            {userOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 1 }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, color: "#161e55" }}>
              Condiciones
            </Typography>
            <Button
              size="small"
              startIcon={<AddCircleOutlineIcon />}
              onClick={addGroup}
              sx={{ textTransform: "none" }}
            >
              Agregar grupo (OR)
            </Button>
          </Stack>

          {groups.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Sin condiciones. Usa «Agregar grupo (OR)» para empezar.
            </Typography>
          ) : null}

          <Stack spacing={2}>
            {groups.map((group, groupIndex) => (
              <React.Fragment key={`conditional-group-${entryIndex}-${groupIndex}`}>
                {groupIndex > 0 ? (
                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <Chip label="O" size="small" sx={{ fontWeight: 700 }} />
                  </Box>
                ) : null}
                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: "12px" }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mb: 1 }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Grupo {groupIndex + 1}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => removeGroup(groupIndex)}
                      aria-label={`Eliminar grupo ${groupIndex + 1}`}
                    >
                      <RemoveCircleOutlineIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                  <Stack spacing={1.5}>
                    {group.conditions.map((condition, conditionIndex) => (
                      <React.Fragment
                        key={`cond-${entryIndex}-${groupIndex}-${conditionIndex}`}
                      >
                        {conditionIndex > 0 ? (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ pl: 0.5 }}
                          >
                            Y
                          </Typography>
                        ) : null}
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                          <FormControl fullWidth size="small">
                            <InputLabel
                              id={`cond-field-${entryIndex}-${groupIndex}-${conditionIndex}`}
                            >
                              Campo
                            </InputLabel>
                            <Select
                              labelId={`cond-field-${entryIndex}-${groupIndex}-${conditionIndex}`}
                              label="Campo"
                              value={condition.field}
                              onChange={(event) =>
                                updateCondition(groupIndex, conditionIndex, {
                                  field: event.target.value,
                                })
                              }
                            >
                              <MenuItem value="">
                                <em>Seleccionar</em>
                              </MenuItem>
                              {fieldOptions.map((opt) => (
                                <MenuItem key={opt.key} value={opt.key}>
                                  {opt.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <FormControl fullWidth size="small" sx={{ minWidth: 140 }}>
                            <InputLabel
                              id={`cond-op-${entryIndex}-${groupIndex}-${conditionIndex}`}
                            >
                              Operador
                            </InputLabel>
                            <Select
                              labelId={`cond-op-${entryIndex}-${groupIndex}-${conditionIndex}`}
                              label="Operador"
                              value={condition.operator}
                              onChange={(event) =>
                                updateCondition(groupIndex, conditionIndex, {
                                  operator: event.target.value,
                                  value: operatorNeedsValue(event.target.value)
                                    ? condition.value
                                    : "",
                                })
                              }
                            >
                              {CONDITIONAL_OPERATORS.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>
                                  {opt.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          {operatorNeedsValue(condition.operator) ? (
                            <TextField
                              label="Valor"
                              size="small"
                              value={condition.value}
                              onChange={(event) =>
                                updateCondition(groupIndex, conditionIndex, {
                                  value: event.target.value,
                                })
                              }
                              fullWidth
                            />
                          ) : (
                            <Box sx={{ display: { xs: "none", sm: "block" }, minWidth: 120 }} />
                          )}
                          <IconButton
                            size="small"
                            onClick={() => removeCondition(groupIndex, conditionIndex)}
                            aria-label="Eliminar condición"
                            sx={{ alignSelf: "center" }}
                          >
                            <RemoveCircleOutlineIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </React.Fragment>
                    ))}
                  </Stack>
                  <Button
                    size="small"
                    startIcon={<AddCircleOutlineIcon />}
                    onClick={() => addConditionToGroup(groupIndex)}
                    sx={{ mt: 1, textTransform: "none" }}
                  >
                    Agregar condición (AND)
                  </Button>
                </Paper>
              </React.Fragment>
            ))}
          </Stack>

          {previewWhen ? (
            <FormHelperText sx={{ mt: 1 }}>
              Expresión generada: <code>{previewWhen}</code>
            </FormHelperText>
          ) : null}
        </Box>

        <Divider />

        {placeholderWarnings.length > 0 ? (
          <Alert severity="warning">
            Placeholders inválidos en el mensaje condicional:{" "}
            {placeholderWarnings.join(", ")}
          </Alert>
        ) : null}

        <TextField
          label="Mensaje condicional"
          value={conditionalMessage}
          onChange={handleConditionalMessageChange}
          fullWidth
          multiline
          minRows={3}
          error={placeholderWarnings.length > 0}
          helperText="Puedes usar placeholders {{...}} como en el mensaje principal."
        />

        {parameterOptions.length > 0 ? (
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, color: "#161e55", mb: 1 }}>
              Parámetros del mensaje condicional
            </Typography>
            <Paper
              variant="outlined"
              sx={{ maxHeight: 180, overflow: "auto", borderRadius: "12px" }}
            >
              <List dense disablePadding>
                {parameterOptions.map((parameter) => (
                  <ListItemButton
                    key={`conditional-param-${entryIndex}-${parameter.value}`}
                    onClick={() => insertConditionalParameter(parameter.value)}
                  >
                    <ListItemText
                      primary={parameter.label}
                      secondary={
                        parameter.description
                          ? `${parameter.value} — ${parameter.description}`
                          : parameter.value
                      }
                    />
                  </ListItemButton>
                ))}
              </List>
            </Paper>
          </Box>
        ) : null}

        {showSyntaxHelp ? (
          <Accordion
            expanded={syntaxExpanded}
            onChange={(_event, expanded) => setSyntaxExpanded(expanded)}
            disableGutters
            elevation={0}
            sx={{ border: "1px solid rgba(0,0,0,0.12)", borderRadius: "12px !important" }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body2">Ayuda de sintaxis condicional</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={1}>
                <Typography variant="body2">
                  <strong>=</strong> Igual (case-insensitive) · <strong>!=</strong> Distinto ·{" "}
                  <strong>?</strong> Existe y no está vacío · <strong>!?</strong> No existe o
                  está vacío · <strong>&gt;, &gt;=, &lt;, &lt;=</strong> Comparación numérica
                </Typography>
                <Typography variant="body2">
                  <strong>;</strong> AND dentro de un grupo · <strong>|</strong> OR entre
                  grupos · <strong>( )</strong> Agrupar (AND tiene prioridad sobre OR)
                </Typography>
                <Typography variant="body2" component="div">
                  Ejemplo: <code>occupation=Comercio;document_number? | client_type=NUEVO</code>
                </Typography>
              </Stack>
            </AccordionDetails>
          </Accordion>
        ) : null}
      </Stack>
    </Paper>
  );
}
